// React
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldText, Toggle } from "components/AtomsV2";
import { BrandLogo, Icon } from "components/ShellV2";

// Assets
import inesctecLogo from "assets/images/inesctec.png";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  selectThemeMode,
  setThemeMode,
} from "state/application/applicationSlice";
import { useLoginMutation, useRefreshMutation } from "state/auth/authApiSlice";
import { selectAuth, setCredentials } from "state/auth/authSlice";
import { useGetLandingStatsQuery } from "state/communities/communitiesApiSlice";
import TermsModal from "./TermsModal";
import AcknowledgmentsModal from "./AcknowledgmentsModal";

function SignIn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const isLoggedIn = auth.user && auth.accessToken;
  const { user } = useParams();
  const { t } = useTranslation(["auth"]);
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";
  const toggleTheme = () => dispatch(setThemeMode(isDark ? "light" : "dark"));

  const [email, setEmail] = useState(() => {
    // Try to load saved email on initial state
    if (!user) {
      const savedEmail = localStorage.getItem("rememberedEmail");
      return savedEmail || "";
    }
    return user;
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState(undefined);
  const [buttonText, setButtonText] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Check if there's a saved email on initial state
    if (!user) {
      const savedEmail = localStorage.getItem("rememberedEmail");
      return !!savedEmail;
    }
    return false;
  });

  const [login] = useLoginMutation();
  const [refresh] = useRefreshMutation();

  const handleLogin = async () => {
    try {
      const loginResponse = await login({ email, password }).unwrap();
      dispatch(setCredentials({ ...loginResponse }));

      // Save or clear email based on remember me
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      return navigate("/communities");
    } catch (err) {
      setButtonText(t("login.submit_again"));

      if (err.data) {
        // Handle different error formats
        const { detail } = err.data;

        // If detail is an array (validation errors), extract the first message
        if (Array.isArray(detail) && detail.length > 0) {
          return setError(detail[0].msg || t("login.errors.generic"));
        }

        // If detail is a string (auth error), use it directly
        if (typeof detail === "string") {
          return setError(detail);
        }

        // Fallback to generic error
        return setError(t("login.errors.generic"));
      }

      return setError(t("login.errors.generic"));
    }
  };

  const handleSubmit = async () => {
    if (isLoggedIn) {
      return navigate("/communities");
    }
    if (email === "") {
      return setError(t("login.errors.email_required"));
    }
    if (password === "") {
      return setError(t("login.errors.password_required"));
    }

    setButtonText(t("login.submit_working"));
    return handleLogin();
  };

  useEffect(() => {
    (async () => {
      if (isLoggedIn) {
        try {
          await refresh().unwrap(); // Wait for refresh to succeed
          navigate("/communities");
        } catch (err) {
          // If refresh fails, user is not authenticated anymore
          dispatch(openNotificationPopup({ notification: err, type: "error" }));
          // Don't navigate to /communities if refresh failed
        }
        setButtonText(t("login.submit_goback"));
      } else {
        setButtonText(t("login.submit_default"));
      }
    })();
  }, [isLoggedIn, t]);

  const { data: stats } = useGetLandingStatsQuery();
  const fmtStat = (n) => (n != null ? Number(n).toLocaleString("pt-PT") : "—");

  const year = new Date().getFullYear();
  const asideStats = [
    [fmtStat(stats?.total_communities), t("login.aside.stat_communities")],
    [fmtStat(stats?.total_members), t("login.aside.stat_members")],
    [fmtStat(stats?.total_resources), t("login.aside.stat_resources")],
  ];

  return (
    <div className="rec-auth">
      <aside className="rec-auth-aside">
        <div className="rec-ring" style={{ width: 520, height: 520, top: -160, right: -160 }} />
        <div className="rec-ring" style={{ width: 340, height: 340, bottom: -120, left: -90 }} />
        <div className="relative flex items-center gap-3">
          <BrandLogo variant="v1" forceWhite height={124} />
        </div>
        <div className="relative">
          <h2 className="text-[34px] font-bold leading-[1.2] tracking-[-0.6px] m-0 mb-[14px]">
            {t("login.aside.headline")}
          </h2>
          <p className="text-[15px] leading-[1.6] text-white/[.78] max-w-[420px] m-0">
            {t("login.aside.copy")}
          </p>
          <div className="flex gap-[30px] mt-8">
            {asideStats.map(([n, l]) => (
              <div key={l}>
                <div className="text-[26px] font-bold tracking-[-0.5px]">{n}</div>
                <div className="text-[12.5px] text-white/[.65] mt-[2px]">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-[12.5px] text-white/[.55]">
          {t("login.aside.copyright", { year })}
        </div>
      </aside>

      <div className="rec-auth-form-wrap">
        <div className="rec-auth-card">
          <div className="flex justify-end mb-2">
            <button
              type="button"
              className="rec-icon-btn"
              onClick={toggleTheme}
              title={t("login.theme_toggle")}
            >
              <Icon name={isDark ? "light_mode" : "dark_mode"} fill />
            </button>
          </div>
          <h1 className="text-[26px] font-bold tracking-[-0.4px] m-0 mb-[6px] text-ink">
            {t("login.title")}
          </h1>
          <p className="text-[14px] text-ink-3 m-0 mb-[26px]">
            {isLoggedIn ? t("login.subtitle_logged_in") : t("login.subtitle")}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            className="flex flex-col gap-4"
          >
            {!isLoggedIn && (
              <>
                <FieldText
                  id="username"
                  label={t("login.email_label")}
                  icon="mail"
                  type="email"
                  value={email}
                  placeholder={t("login.email_label")}
                  onChange={(v) => {
                    setEmail(v);
                    setError(undefined);
                  }}
                />
                <FieldText
                  id="password"
                  label={t("login.password_label")}
                  icon="lock"
                  type="password"
                  reveal
                  value={password}
                  placeholder="••••••••"
                  onChange={(v) => {
                    setPassword(v);
                    setError(undefined);
                  }}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-[9px]">
                    <Toggle
                      id="remember-me"
                      on={rememberMe}
                      onClick={() => setRememberMe((r) => !r)}
                      ariaLabel={t("login.remember_me")}
                    />
                    <button
                      type="button"
                      className="text-[13px] text-ink-2 cursor-pointer font-medium bg-transparent border-0 p-0"
                      onClick={() => setRememberMe((r) => !r)}
                    >
                      {t("login.remember_me")}
                    </button>
                  </div>
                  <Link
                    to="/forgot-password"
                    className="text-[13px] font-semibold no-underline"
                    style={{ color: "var(--accent-deep)" }}
                  >
                    {t("login.forgot_password")}
                  </Link>
                </div>
                {error && (
                  <div
                    className="text-[13px] font-medium text-center"
                    style={{ color: "var(--danger)" }}
                  >
                    {error}
                  </div>
                )}
              </>
            )}
            <Button type="submit" className="h-[46px] text-[15px] mt-1">
              {buttonText}
              <Icon name="arrow_forward" />
            </Button>
          </form>
          {!isLoggedIn && (
            <div className="text-center mt-[22px] text-[13px] text-ink-3">
              <a
                href={`${window.location.origin}/docs`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-[5px] font-semibold no-underline"
                style={{ color: "var(--accent-deep)" }}
              >
                <Icon name="menu_book" style={{ fontSize: 15 }} />
                {t("login.docs_cta")}
              </a>
            </div>
          )}
          {!isLoggedIn && (
            <div className="text-center mt-[10px] text-[12.5px] text-ink-3">
              {t("terms.link_prefix")}
              <button
                type="button"
                className="font-semibold bg-transparent border-0 p-0 cursor-pointer"
                style={{ color: "var(--accent-deep)" }}
                onClick={() => setTermsOpen(true)}
              >
                {t("terms.link")}
              </button>
              {t("terms.link_suffix")}
            </div>
          )}
          <div className="rec-auth-credit">
            <span>{t("login.credit")}</span>
            <img src={inesctecLogo} alt="INESC TEC" />
            <span aria-hidden>·</span>
            <button
              type="button"
              className="inline-flex items-center gap-[5px] font-semibold bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: "var(--accent-deep)", fontSize: "11.5px" }}
              onClick={() => setAckOpen(true)}
            >
              <Icon name="volunteer_activism" style={{ fontSize: 15 }} />
              {t("acknowledgments.link")}
            </button>
          </div>
        </div>
      </div>
      <TermsModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        onAccept={() => setTermsOpen(false)}
      />
      <AcknowledgmentsModal open={ackOpen} onClose={() => setAckOpen(false)} />
    </div>
  );
}

export default SignIn;
